"use client";

import { useCallback, useEffect, useRef, type RefCallback } from "react";

import type { PublicSearchState } from "@/lib/gift-list-search/search-state";
import { acquireLenis, releaseLenis } from "@/lib/motion/lenis-engine";
import { getPublicHeaderBottom } from "@/lib/public-header-metrics";

const LENIS_OPTIONS = {
  lerp: 0.08,
  duration: 1.5,
  smoothWheel: true,
  wheelMultiplier: 1.2,
} as const;

/** Tucks the hairline that divides form and listing under the header's bottom edge. */
const DIVIDER_COVER_PX = 1;
const RESULTS_SCROLL_DURATION_SECONDS = 1.2;
const TOUR_SCROLL_DURATION_SECONDS = 0.72;
const TOUR_SCROLL_TIMEOUT_MS = 1_000;
const CORRECTION_DELAY_MS = 1_400;
const CORRECTION_TOLERANCE_PX = 24;
const MAX_MEASURE_ATTEMPTS = 40;

type LenisInstance = InstanceType<typeof import("lenis").default>;
type SearchPhase = PublicSearchState["phase"];

function easeOutCubic(progress: number): number {
  return 1 - (1 - progress) ** 3;
}

/**
 * Where the top of the results shell must land, measured from the live header
 * instead of a hardcoded height: the header has fluid padding and no height
 * token, so the only reliable offset is the one the layout reports.
 */
function getResultsOffset(): number {
  return Math.max(0, getPublicHeaderBottom() - DIVIDER_COVER_PX);
}

function getTargetTop(element: HTMLElement): number {
  return Math.max(
    0,
    element.getBoundingClientRect().top + window.scrollY - getResultsOffset(),
  );
}

export type ProgrammaticScrollResult = "cancelled" | "completed";

export interface PublicSearchMotionController {
  setResultsElement: RefCallback<HTMLDivElement>;
  setScrollLocked(locked: boolean): void;
  scrollToPosition(top: number): Promise<ProgrammaticScrollResult>;
  cancelProgrammaticScroll(): void;
}

export function usePublicSearchMotion(
  phase: SearchPhase,
  initialSearchVersion: number,
): PublicSearchMotionController {
  const resultsElementRef = useRef<HTMLDivElement>(null);
  const setResultsElement = useCallback<RefCallback<HTMLDivElement>>((element) => {
    resultsElementRef.current = element;
  }, []);
  const lenisRef = useRef<LenisInstance | null>(null);
  const scrollLockedRef = useRef(false);
  const lenisScrollActiveRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const handledInitialSearchVersionRef = useRef(0);
  const layoutFrameRef = useRef<number | null>(null);
  const nativeScrollFrameRef = useRef<number | null>(null);
  const correctionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tourScrollFrameRef = useRef<number | null>(null);
  const tourScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tourScrollResolveRef = useRef<
    ((result: ProgrammaticScrollResult) => void) | null
  >(null);
  const tourScrollTargetRef = useRef<number | null>(null);
  const tourLenisScrollActiveRef = useRef(false);
  const tourScrollVersionRef = useRef(0);

  const finishTourScroll = useCallback((result: ProgrammaticScrollResult) => {
    if (tourScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(tourScrollFrameRef.current);
      tourScrollFrameRef.current = null;
    }
    if (tourScrollTimerRef.current !== null) {
      clearTimeout(tourScrollTimerRef.current);
      tourScrollTimerRef.current = null;
    }
    tourLenisScrollActiveRef.current = false;
    tourScrollTargetRef.current = null;
    const resolve = tourScrollResolveRef.current;
    tourScrollResolveRef.current = null;
    resolve?.(result);
  }, []);

  const cancelProgrammaticScroll = useCallback(() => {
    tourScrollVersionRef.current += 1;
    if (tourScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(tourScrollFrameRef.current);
      tourScrollFrameRef.current = null;
    }
    if (tourScrollTimerRef.current !== null) {
      clearTimeout(tourScrollTimerRef.current);
      tourScrollTimerRef.current = null;
    }
    const lenis = lenisRef.current;
    if (lenis !== null && tourLenisScrollActiveRef.current) {
      lenis.scrollTo(lenis.actualScroll, { immediate: true, force: true });
    }
    tourLenisScrollActiveRef.current = false;
    tourScrollTargetRef.current = null;
    const resolve = tourScrollResolveRef.current;
    tourScrollResolveRef.current = null;
    resolve?.("cancelled");
  }, []);

  const cancelPendingResultScroll = useCallback(() => {
    if (layoutFrameRef.current !== null) {
      window.cancelAnimationFrame(layoutFrameRef.current);
      layoutFrameRef.current = null;
    }
    if (nativeScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(nativeScrollFrameRef.current);
      nativeScrollFrameRef.current = null;
    }
    if (correctionTimerRef.current !== null) {
      clearTimeout(correctionTimerRef.current);
      correctionTimerRef.current = null;
    }
    const lenis = lenisRef.current;
    if (lenis !== null && lenisScrollActiveRef.current) {
      lenis.scrollTo(lenis.actualScroll, { immediate: true, force: true });
    }
    lenisScrollActiveRef.current = false;
  }, []);

  const setScrollLocked = useCallback(
    (locked: boolean) => {
      scrollLockedRef.current = locked;
      const lenis = lenisRef.current;
      if (locked) {
        cancelProgrammaticScroll();
        cancelPendingResultScroll();
        lenis?.stop();
      } else {
        lenis?.start();
      }
    },
    [cancelPendingResultScroll, cancelProgrammaticScroll],
  );

  const scrollToPosition = useCallback(
    (requestedTop: number): Promise<ProgrammaticScrollResult> => {
      cancelProgrammaticScroll();
      if (scrollLockedRef.current) return Promise.resolve("cancelled");
      const maximumTop = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const target = Math.min(maximumTop, Math.max(0, requestedTop));
      if (Math.abs(target - window.scrollY) <= 1) {
        return Promise.resolve("completed");
      }
      if (reducedMotionRef.current) {
        window.scrollTo(0, target);
        return Promise.resolve("completed");
      }

      const version = tourScrollVersionRef.current;
      tourScrollTargetRef.current = target;
      return new Promise((resolve) => {
        tourScrollResolveRef.current = resolve;
        const complete = () => {
          if (version !== tourScrollVersionRef.current) return;
          finishTourScroll("completed");
        };
        tourScrollTimerRef.current = setTimeout(complete, TOUR_SCROLL_TIMEOUT_MS);

        const lenis = lenisRef.current;
        if (lenis !== null) {
          lenis.resize();
          tourLenisScrollActiveRef.current = true;
          lenis.scrollTo(target, {
            duration: TOUR_SCROLL_DURATION_SECONDS,
            easing: easeOutCubic,
            force: true,
            onComplete: complete,
          });
          return;
        }

        const start = window.scrollY;
        const distance = target - start;
        const duration = Math.min(720, Math.max(360, Math.abs(distance) * 0.48));
        const startedAt = performance.now();
        const step = (now: number) => {
          if (version !== tourScrollVersionRef.current) return;
          const progress = Math.min(1, (now - startedAt) / duration);
          window.scrollTo(0, start + distance * easeOutCubic(progress));
          if (progress < 1) {
            tourScrollFrameRef.current = window.requestAnimationFrame(step);
          } else {
            complete();
          }
        };
        tourScrollFrameRef.current = window.requestAnimationFrame(step);
      });
    },
    [cancelProgrammaticScroll, finishTourScroll],
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    reducedMotionRef.current = mediaQuery?.matches ?? false;

    let mounted = true;
    let importVersion = 0;
    /** Whether this mount currently holds a reference to the shared engine. */
    let heldLenis = false;

    const stopLenis = (finishAtResults: boolean) => {
      importVersion += 1;

      const lenis = lenisRef.current;
      if (
        lenis !== null &&
        (lenisScrollActiveRef.current || tourLenisScrollActiveRef.current)
      ) {
        lenis.scrollTo(lenis.actualScroll, { immediate: true, force: true });
      }
      lenisScrollActiveRef.current = false;
      tourLenisScrollActiveRef.current = false;
      /*
       * Hands the reference back; it does not destroy. The engine is the
       * product's one (`lib/motion/lenis-engine.ts`) and another surface may
       * still be holding it — destroying it here is what the PDP's immortal
       * instance made look harmless, when in fact each page was running its own
       * and they fought over the document's scroll every frame.
       */
      lenisRef.current = null;
      if (heldLenis) {
        heldLenis = false;
        releaseLenis();
      }

      if (finishAtResults) {
        const element = resultsElementRef.current;
        if (element !== null) window.scrollTo(0, getTargetTop(element));
      }
    };

    const startLenis = () => {
      if (!mounted || reducedMotionRef.current || lenisRef.current !== null) return;
      const version = ++importVersion;

      heldLenis = true;
      void acquireLenis(LENIS_OPTIONS)
        .then((lenis) => {
          if (!mounted || reducedMotionRef.current || version !== importVersion) {
            // Acquired and no longer wanted: give it straight back, or the count
            // never returns to zero and the engine never dies.
            if (heldLenis) {
              heldLenis = false;
              releaseLenis();
            }
            return;
          }
          if (lenis === null) return;

          lenisRef.current = lenis;
          if (scrollLockedRef.current) lenis.stop();
        })
        .catch(() => {
          // Smooth wheel is progressive enhancement. Programmatic result scroll
          // still uses the native, eased fallback below when Lenis cannot load.
        });
    };

    const handleMotionPreference = (event: MediaQueryListEvent) => {
      reducedMotionRef.current = event.matches;
      if (event.matches) {
        const hadProgrammaticScroll =
          lenisScrollActiveRef.current ||
          layoutFrameRef.current !== null ||
          nativeScrollFrameRef.current !== null ||
          correctionTimerRef.current !== null;
        const tourTarget = tourScrollTargetRef.current;
        cancelPendingResultScroll();
        cancelProgrammaticScroll();
        stopLenis(hadProgrammaticScroll);
        if (tourTarget !== null) window.scrollTo(0, tourTarget);
      } else {
        startLenis();
      }
    };

    mediaQuery?.addEventListener("change", handleMotionPreference);
    startLenis();

    return () => {
      mounted = false;
      mediaQuery?.removeEventListener("change", handleMotionPreference);
      cancelProgrammaticScroll();
      stopLenis(false);
    };
  }, [cancelPendingResultScroll, cancelProgrammaticScroll]);

  useEffect(() => {
    function scrollWithNativeFallback(element: HTMLElement) {
      const start = window.scrollY;
      const target = getTargetTop(element);
      const distance = target - start;
      const duration = Math.min(820, Math.max(460, Math.abs(distance) * 0.48));
      const startedAt = performance.now();

      const step = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        window.scrollTo(0, start + distance * easeOutCubic(progress));
        if (progress < 1) {
          nativeScrollFrameRef.current = window.requestAnimationFrame(step);
        } else {
          nativeScrollFrameRef.current = null;
        }
      };

      nativeScrollFrameRef.current = window.requestAnimationFrame(step);
    }

    function scrollToResults(element: HTMLElement) {
      if (reducedMotionRef.current) {
        window.scrollTo(0, getTargetTop(element));
        return;
      }

      const lenis = lenisRef.current;
      if (lenis === null) {
        scrollWithNativeFallback(element);
        return;
      }

      lenis.resize();
      lenisScrollActiveRef.current = true;
      lenis.scrollTo(element, {
        offset: -getResultsOffset(),
        duration: RESULTS_SCROLL_DURATION_SECONDS,
        easing: easeOutCubic,
        onComplete: () => {
          lenisScrollActiveRef.current = false;
        },
      });

      // The scroll starts with the skeletons on screen; this pass re-anchors the
      // shell once the real cards have replaced them.
      correctionTimerRef.current = setTimeout(() => {
        correctionTimerRef.current = null;
        const residual = element.getBoundingClientRect().top - getResultsOffset();
        if (Math.abs(residual) <= CORRECTION_TOLERANCE_PX) return;
        lenis.resize();
        lenisScrollActiveRef.current = true;
        lenis.scrollTo(element, {
          offset: -getResultsOffset(),
          duration: 0.6,
          easing: easeOutCubic,
          onComplete: () => {
            lenisScrollActiveRef.current = false;
          },
        });
      }, CORRECTION_DELAY_MS);
    }

    function queueMeasuredScroll(attempt = 0) {
      const element = resultsElementRef.current;
      if (element === null) return;

      if (element.getBoundingClientRect().height === 0 && attempt < MAX_MEASURE_ATTEMPTS) {
        layoutFrameRef.current = window.requestAnimationFrame(() => {
          queueMeasuredScroll(attempt + 1);
        });
        return;
      }

      layoutFrameRef.current = window.requestAnimationFrame(() => {
        layoutFrameRef.current = window.requestAnimationFrame(() => {
          layoutFrameRef.current = null;
          const currentElement = resultsElementRef.current;
          if (currentElement !== null) scrollToResults(currentElement);
        });
      });
    }

    // Clearing the search, locking the scroll behind the drawer or paginating all
    // drop the destination. `load-more` matters most: the correction pass runs long
    // after the movement ends and would re-anchor the listing mid-reading.
    if (scrollLockedRef.current || phase === "idle" || phase === "load-more") {
      cancelPendingResultScroll();
      return;
    }

    // The movement follows the skeletons, not the payload: `loading` is the first
    // phase after a submit or retry, so the wait happens already on the listing.
    // Terminal phases stay eligible as a safety net — a locked scroll, or a phase
    // committed without an intermediate `loading` render.
    if (initialSearchVersion > handledInitialSearchVersionRef.current) {
      handledInitialSearchVersionRef.current = initialSearchVersion;
      cancelPendingResultScroll();
      queueMeasuredScroll();
    }

    // No teardown on purpose: this effect re-runs on every phase transition, and
    // the payload replacing the skeletons is one of them — cancelling here would
    // cut the movement short.
  }, [cancelPendingResultScroll, initialSearchVersion, phase]);

  // Frames and timers outlive React, so teardown belongs to the unmount.
  useEffect(() => cancelPendingResultScroll, [cancelPendingResultScroll]);

  return {
    cancelProgrammaticScroll,
    scrollToPosition,
    setResultsElement,
    setScrollLocked,
  };
}
